import { BaseResponse, MessageResponse, RequestConfig } from 'common/types/api'
import axios from 'core/api'

import { buildUrlWithParams } from 'utils/url'

import {
  CreateFeedProgramResponse,
  GeneralType,
  Brand,
  Species,
  Breed,
  BreedBrand,
  BreedProduct,
  Markets,
  Market,
  MarketsVariables,
  BreedBrandParams,
  BreedProductParams,
  FeedPrograms,
  FeedProgramsVariables,
  FeedProgram,
  PondResponse,
  CycleHistory,
  PondName,
} from './types'

export const POND_REQUEST_CONFIG: RequestConfig = {
  getPonds: { method: 'GET', url: '/ponds' },
  createPond: { method: 'POST', url: '/ponds' },
  editPond: { method: 'PATCH', url: '/ponds' },
  removePond: { method: 'DELETE', url: '/ponds' },
  getCompletedPonds: { method: 'GET', url: '/completed-ponds' },
  filterCount: { method: 'GET', url: '/ponds/filter-count' },
  editPinPond: { method: 'PATCH', url: '/pin' },
}

export const CYCLE_REQUEST_CONFIG: RequestConfig = {
  getCycles: { method: 'GET', url: '/cycles/history' },
  getCycleById: { method: 'GET', url: '/cycles' },
  createCycle: { method: 'POST', url: '/cycles' },
  createCycleWithCredit: { method: 'POST', url: '/cycles/purchase' },
  editCycle: { method: 'PATCH' },
  completeCycle: { method: 'PATCH', url: '/complete' },
  removeCycle: { method: 'DELETE' },
}

export const DASHBOARD_REQUEST_CONFIG: RequestConfig = {
  getDashboard: { method: 'GET', url: '/dashboard' },
  getDashboardFinance: { method: 'GET', url: '/dashboard/finance' },
  getPondDasboard: { method: 'GET', url: '/pond-dashboard' },
  getLineChart: { method: 'GET', url: '/dashboard/line-chart' },
  getRevenueLineChart: { method: 'GET', url: '/dashboard/revenue-cost-trend' },
}

export const EXPORT_REQUEST_CONFIG: RequestConfig = {
  exportSummary: { method: 'GET', url: '/summary-pdf', responseType: 'blob' },
  exportDaily: { method: 'GET', url: '/daily-pdf', responseType: 'blob' },
  exportFeatures: { method: 'GET', url: '/export', responseType: 'blob' },
}

/**
 * Pond-scoped exports. The multi-cycle summary export takes a list of cycle ids, so
 * it hangs off the pond rather than a single cycle.
 */
export const POND_EXPORT_REQUEST_CONFIG: RequestConfig = {
  exportCyclesSummary: { method: 'GET', url: '/cycles/summary-export', responseType: 'blob' },
}

export const POND_NOTE_REQUEST_CONFIG: RequestConfig = {
  createPondNote: { method: 'POST', url: '/notes' },
}

export const getPondTypes = () => axios.get<BaseResponse<GeneralType[]>>('/pond-types')

export const getShrimpBrands = () => axios.get<BaseResponse<Brand[]>>('/shrimp-brands')

export const getShrimpTypes = (brand: number) =>
  axios.get<BaseResponse<GeneralType[]>>(buildUrlWithParams('/shrimp-types', { brand }))

export const getSpecies = () => axios.get<BaseResponse<Species[]>>('/species')

export const getBreed = (speciesId: number | null) =>
  axios.get<BaseResponse<Breed[]>>(`/species/${speciesId}/breed`)

export const getBreedBrand = ({ speciesId, breedId }: BreedBrandParams) =>
  axios.get<BaseResponse<BreedBrand[]>>(`/species/${speciesId}/breed/${breedId}/breed-brand`)

export const getBreedProduct = ({ speciesId, breedId, breedBrandId }: BreedProductParams) =>
  axios.get<BaseResponse<BreedProduct[]>>(
    `/species/${speciesId}/breed/${breedId}/breed-brand/${breedBrandId}/breed-products`
  )

export const getFeedPrograms = () =>
  axios.get<BaseResponse<FeedPrograms[]>>(`/shrimp/feed-programs`)
export const getFeedProgramsById = (feedProgramId?: number) =>
  axios.get<BaseResponse<FeedProgram>>(`/shrimp/feed-programs/${feedProgramId}`)

export const editFeedPrograms = ({ feedProgramId, body }: FeedProgramsVariables) =>
  axios.patch<MessageResponse>(`/shrimp/feed-programs/${feedProgramId}`, {
    ...body,
  })
export const createFeedPrograms = ({ body }: FeedProgramsVariables) =>
  axios.post<BaseResponse<CreateFeedProgramResponse>>(`/shrimp/feed-programs`, { ...body })
export const deleteFeedProgram = (feedProgramId: number) =>
  axios.delete<MessageResponse>(`/shrimp/feed-programs/${feedProgramId}`)

export const getMarkets = (speciesId: number | null) =>
  axios.get<BaseResponse<Markets[]>>(buildUrlWithParams('/markets', { speciesId }))

export const getMarketsById = (marketId: number | null) =>
  axios.get<BaseResponse<Market>>(`/markets/${marketId}`)

export const editMarkets = ({ marketId, body }: MarketsVariables) =>
  axios.patch<MessageResponse>(`/markets/${marketId}`, { ...body })

export const createMarkets = ({ body }: MarketsVariables) =>
  axios.post<MessageResponse>(`/markets`, { ...body })

export const getPondAreaUnits = () => axios.get<BaseResponse<any[]>>(`/units/pond-area`)

export const getPondByFarm = (farmId: string) =>
  axios.get<BaseResponse<PondResponse>>(`/farms/${farmId}/ponds`)

export const getCycleByFarmByPond = (farmId: string, pondId: string) =>
  axios.get<BaseResponse<CycleHistory[]>>(`/farms/${farmId}/ponds/${pondId}/cycles`)

export const getAllPondNames = (farmId: string) =>
  axios.get<BaseResponse<PondName[]>>(`/farms/${farmId}/ponds/name`)
